import { useCallback, useMemo } from 'react';
import { Box, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { BarChart, ChartsAxisData, BarItemIdentifier } from '@mui/x-charts';
import {
  buildValueFormatter,
  resolveDataKey,
} from './controllers/axisController';
import { useChartAxes } from './hooks/useChartAxes';
import { useLegendDisplayMode } from './hooks/useLegendDisplayMode';
import { LegendToggleButton } from './components/LegendToggleButton';
import { CategoryLegend } from './components/CategoryLegend';
import { BarChartTooltip } from './components/BarChartTooltip';
import { BarChartTooltipProvider } from './components/barChartTooltipContext';
import { SmartBarLabel } from './components/SmartBarLabel';
import type { HorizontalBarChartProps, HorizontalBarDatum } from './types';

const EMPTY_STATE_HEIGHT = 160;

const DEFAULT_HEIGHT = 350;
const DEFAULT_Y_LABEL_WIDTH = 150;

export const HorizontalBarChart = ({
  data,
  valueMode,
  defaultLegendDisplay = 'axis',
  legendDisplay: controlledLegendDisplay,
  onLegendDisplayChange,
  enableLegendToggle = false,
  showValueLabels = false,
  valueLabelFormatter,
  height = DEFAULT_HEIGHT,
  yAxisLabelWidth = DEFAULT_Y_LABEL_WIDTH,
  colors,
  onBarClick,
  tooltipRenderer,
  xAxisLabel,
}: HorizontalBarChartProps) => {
  const { t } = useTranslation();
  const { mode: legendDisplay, toggle } = useLegendDisplayMode({
    defaultMode: defaultLegendDisplay,
    controlledValue: controlledLegendDisplay,
    onChange: onLegendDisplayChange,
  });

  const { xAxis, yAxis } = useChartAxes({
    data,
    valueMode,
    legendDisplay,
    yAxisLabelWidth,
    xAxisLabel,
  });

  const tooltipConfig = useMemo(
    () => ({ data, renderContent: tooltipRenderer }),
    [data, tooltipRenderer],
  );

  const handleAxisClick = useCallback(
    (event: MouseEvent, axis: ChartsAxisData | null) => {
      if (!onBarClick || axis === null) return;
      const target = event.target as SVGTextElement | null;
      const labelText = target?.textContent;
      if (!labelText) return;
      const index = data.findIndex((d) => d.name === labelText);
      if (index !== -1) onBarClick(data[index], index);
    },
    [data, onBarClick],
  );

  const handleItemClick = useCallback(
    (_event: React.MouseEvent<SVGElement>, item: BarItemIdentifier) => {
      if (!onBarClick) return;
      const index = item.dataIndex;
      if (typeof index !== 'number') return;
      const datum = data[index] as HorizontalBarDatum | undefined;
      if (!datum) return;
      onBarClick(datum, index);
    },
    [data, onBarClick],
  );

  const seriesColorMap = colors
    ? {
        type: 'ordinal' as const,
        colors: [...colors],
      }
    : undefined;

  // An empty plot keeps its full height and centres MUI's message over the bar
  // area, which a narrow card clips at both ends. Say it across the whole width.
  if (data.length === 0) {
    return (
      <Box
        sx={{
          minHeight: EMPTY_STATE_HEIGHT,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          textAlign: 'center',
          px: 2,
        }}
      >
        <Typography
          variant='body2'
          color='text.secondary'
        >
          {t('noDataToDisplay', 'No data to display')}
        </Typography>
      </Box>
    );
  }

  return (
    <BarChartTooltipProvider value={tooltipConfig}>
      <Box position='relative'>
        {enableLegendToggle && controlledLegendDisplay === undefined ? (
          <Box
            sx={{
              position: 'absolute',
              top: 0,
              left: 0,
              zIndex: 10,
            }}
          >
            <LegendToggleButton
              mode={legendDisplay}
              onToggle={toggle}
            />
          </Box>
        ) : null}

        <BarChart
          height={height}
          margin={{ left: 0, right: 40, top: 10, bottom: 40 }}
          dataset={data as unknown as Record<string, number | string>[]}
          layout='horizontal'
          borderRadius={4}
          disableAxisListener
          axisHighlight={{ x: 'none', y: 'none' }}
          yAxis={[
            {
              scaleType: 'band',
              width: yAxis.width,
              position: 'left',
              dataKey: 'id',
              valueFormatter: (bandId) =>
                yAxis.valueFormatter(bandId as string | number),
              ...(seriesColorMap ? { colorMap: seriesColorMap } : {}),
              tickLabelStyle: { cursor: onBarClick ? 'pointer' : 'default' },
            },
          ]}
          xAxis={[
            {
              height: xAxis.height,
              ...(xAxis.min !== undefined ? { min: xAxis.min } : {}),
              ...(xAxis.max !== undefined ? { max: xAxis.max } : {}),
              ...(xAxis.tickMinStep !== undefined
                ? { tickMinStep: xAxis.tickMinStep }
                : {}),
              ...(xAxis.label ? { label: xAxis.label } : {}),
              tickLabelStyle: { fontSize: 12 },
              valueFormatter: (value: number | null) =>
                value === null ? '' : xAxis.valueFormatter(value),
              tickLabelInterval: xAxis.tickLabelInterval,
            },
          ]}
          series={[
            {
              id: 'series-1',
              dataKey: resolveDataKey(valueMode),
              highlightScope: { fade: 'global', highlight: 'item' },
              type: 'bar',
              ...(showValueLabels
                ? {
                    barLabel: (item: { dataIndex?: number }) => {
                      const idx = item.dataIndex;
                      if (typeof idx !== 'number') return null;
                      const datum = data[idx] as HorizontalBarDatum | undefined;
                      if (!datum) return null;
                      if (valueLabelFormatter)
                        return valueLabelFormatter(datum);
                      const value =
                        resolveDataKey(valueMode) === 'percentValue'
                          ? datum.percentValue
                          : datum.rawValue;
                      return buildValueFormatter(valueMode)(value);
                    },
                    barLabelPlacement: 'outside' as const,
                  }
                : {}),
            },
          ]}
          onAxisClick={handleAxisClick}
          onItemClick={handleItemClick}
          slots={{
            tooltip: BarChartTooltip,
            ...(showValueLabels ? { barLabel: SmartBarLabel } : {}),
          }}
          sx={{
            '& .MuiBarElement-root:hover': {
              filter: 'brightness(0.8)',
            },
            '& .MuiChartsLegend-root': { display: 'none' },
            '& rect[role="graphics-symbol"]': onBarClick
              ? {
                  cursor: 'pointer',
                  '&:hover': { filter: 'brightness(0.8)' },
                }
              : {},
          }}
        />

        {legendDisplay === 'legend' ? (
          <CategoryLegend
            data={data}
            colors={colors}
          />
        ) : null}
      </Box>
    </BarChartTooltipProvider>
  );
};
